import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './paid-student-dashboard.css';

const serverBase = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const mediaUrl = (value) => {
  const path = String(value || '').trim();
  if (!path) return '';
  return /^(https?:|data:|blob:)/i.test(path) ? path : `${serverBase}${path.startsWith('/') ? '' : '/'}${path}`;
};
const formatMoney = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;
const coursePrice = (course) => {
  const values = [course?.discountedPrice, course?.fees?.discounted, course?.totalFees, course?.fees?.original]
    .map(Number)
    .filter((value) => Number.isFinite(value) && value > 0);
  return values[0] || 0;
};
const rotateDaily = (items, salt = 0) => {
  if (!items.length) return [];
  const dayNumber = Math.floor(Date.now() / 86400000);
  const offset = (dayNumber + salt) % items.length;
  return [...items.slice(offset), ...items.slice(0, offset)];
};
const courseIcon = (course) => {
  const text = `${course?.courseName || ''} ${course?.category || ''}`.toLowerCase();
  if (/python/.test(text)) return 'bi-filetype-py';
  if (/data|analytics/.test(text)) return 'bi-bar-chart-line-fill';
  if (/ai|machine|ml/.test(text)) return 'bi-cpu-fill';
  if (/design|ui|ux|figma/.test(text)) return 'bi-palette-fill';
  if (/cloud|aws/.test(text)) return 'bi-cloud-fill';
  if (/security|cyber/.test(text)) return 'bi-shield-lock-fill';
  if (/java|mern|web|full.?stack|develop/.test(text)) return 'bi-code-slash';
  return 'bi-mortarboard-fill';
};

function CourseArtwork({ course, className = '' }) {
  const image = mediaUrl(course?.thumbnailUrl || course?.promoImageUrl);
  return (
    <div className={`paid-dashboard-course-art ${className}`.trim()} style={{ '--course-accent': course?.courseColor || '#4f46e5' }}>
      {image ? <img src={image} alt="" /> : <i className={`bi ${courseIcon(course)}`} aria-hidden="true" />}
    </div>
  );
}

export default function PaidStudentDashboard({ refreshKey }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const railRef = useRef(null);
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enquiryCourse, setEnquiryCourse] = useState(null);
  const [enquiryForm, setEnquiryForm] = useState({ name: '', phone: '', email: '', collegeName: '', area: '', notes: '' });
  const [enquiryState, setEnquiryState] = useState({ saving: false, error: '', success: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dashboardResult, catalogResult] = await Promise.all([
        api.get('/dashboard/training-institute/student'),
        api.get('/training/memberships/catalog').catch(() => ({ data: { data: [] } })),
      ]);
      setData(dashboardResult.data?.data || {});
      const payload = catalogResult.data || {};
      const rows = Array.isArray(payload.records) ? payload.records : payload.data;
      setCatalog(Array.isArray(rows) ? rows : (Array.isArray(rows?.records) ? rows.records : []));
    } catch (_) {
      setData({});
      setCatalog([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const courses = useMemo(() => (Array.isArray(data?.myCourses) ? data.myCourses : []), [data?.myCourses]);
  const currentCourse = courses.find((course) => Number(course.progress || 0) < 100) || courses[0] || null;
  const enrolledIds = useMemo(() => new Set(courses.map((course) => String(course.courseId || '')).filter(Boolean)), [courses]);
  const paidPrograms = useMemo(() => catalog
    .filter((course) => coursePrice(course) > 0 && !enrolledIds.has(String(course._id || '')))
    .slice(0, 12), [catalog, enrolledIds]);
  const dailyPaidPrograms = useMemo(() => rotateDaily(paidPrograms), [paidPrograms]);
  const upcomingBatchCourses = useMemo(() => rotateDaily(
    paidPrograms.filter((course) => ['live', 'hybrid'].includes(String(course.deliveryMode || '').toLowerCase())),
    3
  ).slice(0, 2), [paidPrograms]);
  const internshipCourses = useMemo(() => rotateDaily(
    paidPrograms.filter((course) => /internship|project|practical|placement/.test([
      course.courseName,
      course.category,
      course.shortDescription,
      course.description,
      course.notes,
    ].join(' ').toLowerCase())),
    7
  ).slice(0, 2), [paidPrograms]);
  const activity = (Array.isArray(data?.learningActivity) ? data.learningActivity : []).slice(-7);
  const fees = data?.fees || {};
  const enrollment = data?.enrollments || {};

  const openCourse = (course) => {
    const courseId = course?.courseId || course?._id;
    navigate(courseId ? `/course-content?courseId=${encodeURIComponent(courseId)}` : '/course-content');
  };
  const openEnquiry = (course) => {
    setEnquiryCourse(course);
    setEnquiryForm({
      name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
      phone: user?.phone || user?.mobile || '',
      email: user?.email || '',
      collegeName: user?.collegeName || '',
      area: user?.area || '',
      notes: '',
    });
    setEnquiryState({ saving: false, error: '', success: false });
  };
  const closeEnquiry = () => {
    if (enquiryState.saving) return;
    setEnquiryCourse(null);
    setEnquiryState({ saving: false, error: '', success: false });
  };
  const updateEnquiry = (field, value) => setEnquiryForm((current) => ({ ...current, [field]: value }));
  const submitEnquiry = async (event) => {
    event.preventDefault();
    const name = String(enquiryForm.name || '').trim();
    const phone = String(enquiryForm.phone || '').trim();
    const email = String(enquiryForm.email || '').trim().toLowerCase();
    if (!name || !phone || !email) {
      setEnquiryState({ saving: false, success: false, error: 'Name, mobile number, and email are required.' });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEnquiryState({ saving: false, success: false, error: 'Enter a valid email address.' });
      return;
    }
    setEnquiryState({ saving: true, error: '', success: false });
    try {
      await api.post('/training/on-demand/interest/course', {
        courseId: enquiryCourse?._id,
        name,
        phone,
        email,
        collegeName: String(enquiryForm.collegeName || '').trim(),
        area: String(enquiryForm.area || '').trim(),
        notes: String(enquiryForm.notes || '').trim(),
        interestType: 'paid_course',
      });
      setEnquiryState({ saving: false, error: '', success: true });
    } catch (error) {
      setEnquiryState({ saving: false, success: false, error: error.response?.data?.message || 'Could not submit your enquiry. Please try again.' });
    }
  };
  const scrollPrograms = (direction) => railRef.current?.scrollBy({ left: direction * 320, behavior: 'smooth' });

  if (loading && !data) {
    return <div className="paid-student-dashboard-loading"><span className="spinner-border spinner-border-sm" /> Loading your learning dashboard...</div>;
  }

  return (
    <div className="paid-student-dashboard">
      <header className="paid-dashboard-greeting">
        <div>
          <h1>Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, {user?.firstName || 'Learner'}</h1>
          <p>Continue your course and keep moving toward your career goal.</p>
        </div>
        <span className="paid-dashboard-status"><i className="bi bi-lightning-charge-fill" /> {Number(data?.avgProgress || 0)}% average progress</span>
      </header>

      <section className="paid-dashboard-section paid-dashboard-programs paid-dashboard-recommended">
          <div className="paid-dashboard-section-heading">
            <div>
              <span className="paid-dashboard-kicker">Other paid courses</span>
              <h2>Recommended for You</h2>
              <p>Explore career-focused programs available in the current course catalog.</p>
            </div>
            <div className="paid-dashboard-rail-controls">
              <button type="button" aria-label="Previous recommended courses" onClick={() => scrollPrograms(-1)}><i className="bi bi-chevron-left" /></button>
              <button type="button" aria-label="Next recommended courses" onClick={() => scrollPrograms(1)}><i className="bi bi-chevron-right" /></button>
            </div>
          </div>
          {dailyPaidPrograms.length ? (
            <div className="paid-dashboard-program-rail" ref={railRef}>
              {dailyPaidPrograms.map((course) => (
              <article key={course._id} className="paid-dashboard-program-card">
                <CourseArtwork course={course} />
                <div className="paid-dashboard-program-body">
                  <span className="paid-dashboard-program-badge"><i className="bi bi-stars" /> Career program</span>
                  <h3>{course.courseName || 'Program'}</h3>
                  <p>{course.shortDescription || course.category || 'Build practical, career-ready skills.'}</p>
                  <div className="paid-dashboard-program-meta">
                    <span><i className="bi bi-clock" /> {course.numberOfMonths ? `${course.numberOfMonths} months` : `${course.totalHours || 0} hours`}</span>
                    <span><i className="bi bi-journal-text" /> {Number(course.totalLessons || 0)} lessons</span>
                  </div>
                  <div className="paid-dashboard-program-action">
                    <span><i className="bi bi-headset" /> Get course guidance</span>
                    <button type="button" onClick={() => openEnquiry(course)}>Enquire Now <i className="bi bi-arrow-right" /></button>
                  </div>
                </div>
              </article>
              ))}
            </div>
          ) : (
            <div className="paid-dashboard-program-empty">
              <i className="bi bi-collection-play" />
              <span>New paid courses will appear here when they are published.</span>
            </div>
          )}
      </section>

      <div className="paid-dashboard-upcoming-grid">
        <section className="paid-dashboard-upcoming paid-dashboard-upcoming-batches">
          <div className="paid-dashboard-upcoming-heading">
            <span><i className="bi bi-calendar2-week-fill" /></span>
            <div><small>Plan your next step</small><h2>Upcoming New Batches</h2></div>
          </div>
          <div className="paid-dashboard-upcoming-list">
            {upcomingBatchCourses.length ? upcomingBatchCourses.map((course) => (
              <article key={`batch-${course._id}`}>
                <span className="paid-dashboard-upcoming-icon"><i className={`bi ${courseIcon(course)}`} /></span>
                <div><h3>{course.courseName || 'Career program'}</h3><p><i className="bi bi-camera-video-fill" /> {course.deliveryMode === 'hybrid' ? 'Hybrid learning' : 'Live instructor-led'}</p></div>
                <button type="button" onClick={() => openEnquiry(course)}>Enquire <i className="bi bi-arrow-right" /></button>
              </article>
            )) : <div className="paid-dashboard-upcoming-empty">No new live batches are currently published.</div>}
          </div>
        </section>

        <section className="paid-dashboard-upcoming paid-dashboard-upcoming-internship">
          <div className="paid-dashboard-upcoming-heading">
            <span><i className="bi bi-briefcase-fill" /></span>
            <div><small>Learn through practice</small><h2>Upcoming Internship Classes</h2></div>
          </div>
          <div className="paid-dashboard-upcoming-list">
            {internshipCourses.length ? internshipCourses.map((course) => (
              <article key={`internship-${course._id}`}>
                <span className="paid-dashboard-upcoming-icon"><i className={`bi ${courseIcon(course)}`} /></span>
                <div><h3>{course.courseName || 'Internship program'}</h3><p><i className="bi bi-kanban-fill" /> Practical career training</p></div>
                <button type="button" onClick={() => openEnquiry(course)}>Enquire <i className="bi bi-arrow-right" /></button>
              </article>
            )) : <div className="paid-dashboard-upcoming-empty">Internship classes will appear when they are published.</div>}
          </div>
        </section>
      </div>

      {currentCourse ? (
        <section className="paid-dashboard-hero">
          <div className="paid-dashboard-hero-copy">
            <span className="paid-dashboard-eyebrow"><i className="bi bi-play-circle-fill" /> Continue Learning</span>
            <h2>{currentCourse.courseName || 'Your course'}</h2>
            <p>{currentCourse.shortDescription || `${currentCourse.deliveryMode === 'on_demand' ? 'On-demand' : 'Instructor-led'} learning · ${currentCourse.totalLessons || 0} lessons`}</p>
            <div className="paid-dashboard-progress-line"><strong>{Number(currentCourse.progress || 0)}% complete</strong><span>{Number(currentCourse.completedLessons || 0)} of {Number(currentCourse.totalLessons || 0)} lessons</span></div>
            <div className="paid-dashboard-progress"><span style={{ width: `${Math.max(0, Math.min(100, Number(currentCourse.progress || 0)))}%` }} /></div>
            <button type="button" onClick={() => openCourse(currentCourse)}>Continue Learning <i className="bi bi-arrow-right" /></button>
          </div>
          <CourseArtwork course={currentCourse} className="paid-dashboard-hero-art" />
        </section>
      ) : (
        <section className="paid-dashboard-empty"><i className="bi bi-journal-bookmark" /><div><h2>Your learning dashboard is ready</h2><p>Your enrolled course will appear here when access is assigned.</p></div></section>
      )}

      <section className="paid-dashboard-overview" aria-label="Learning overview">
        {[
          ['bi-journals', enrollment.total || 0, 'Enrolled courses', '#4f46e5'],
          ['bi-check-circle-fill', enrollment.completed || 0, 'Completed', '#16a34a'],
          ['bi-hourglass-split', enrollment.pending || 0, 'Pending lessons', '#f97316'],
          ['bi-graph-up-arrow', `${Number(data?.avgProgress || 0)}%`, 'Overall progress', '#0284c7'],
        ].map(([icon, value, label, color]) => <article key={label} style={{ '--metric-color': color }}><span><i className={`bi ${icon}`} /></span><div><strong>{value}</strong><small>{label}</small></div></article>)}
      </section>

      {!!courses.length && (
        <section className="paid-dashboard-section">
          <div className="paid-dashboard-section-heading"><div><h2>My Courses</h2><p>Resume your enrolled programs from where you stopped.</p></div><button type="button" onClick={() => navigate('/course-content')}>View all <i className="bi bi-chevron-right" /></button></div>
          <div className="paid-dashboard-my-courses">
            {courses.map((course) => (
              <button type="button" key={course._id} onClick={() => openCourse(course)}>
                <CourseArtwork course={course} />
                <div className="paid-dashboard-course-copy"><h3>{course.courseName || 'Course'}</h3><p>{course.batchNumber ? `Batch ${course.batchNumber}` : (course.category || 'Professional program')}</p><div><span>{Number(course.completedLessons || 0)}/{Number(course.totalLessons || 0)} lessons</span><strong>{Number(course.progress || 0)}%</strong></div><div className="paid-dashboard-progress"><span style={{ width: `${Math.max(0, Math.min(100, Number(course.progress || 0)))}%` }} /></div></div>
                <i className="bi bi-arrow-right-circle-fill" />
              </button>
            ))}
          </div>
        </section>
      )}

      <div className="paid-dashboard-lower-grid">
        <section className="paid-dashboard-panel"><div className="paid-dashboard-section-heading"><div><h2>Learning Activity</h2><p>Your latest seven days of course activity.</p></div></div><div className="paid-dashboard-activity">{activity.map((day) => <div key={day.key}><span className={Number(day.pct || 0) > 0 ? 'is-active' : ''} style={{ '--activity': `${Math.max(12, Number(day.pct || 0))}%` }}><i className={Number(day.pct || 0) > 0 ? 'bi bi-check-lg' : ''} /></span><small>{new Date(`${day.key}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 1)}</small></div>)}</div></section>
        <section className="paid-dashboard-panel paid-dashboard-fees"><div className="paid-dashboard-section-heading"><div><h2>Course Fee Summary</h2><p>Your existing enrollment payment status.</p></div></div><div><span><small>Course fees</small><strong>{formatMoney(fees.totalFees)}</strong></span><span><small>Paid</small><strong className="text-success">{formatMoney(fees.paid)}</strong></span><span><small>Balance</small><strong className={Number(fees.balance || 0) > 0 ? 'text-danger' : 'text-success'}>{formatMoney(fees.balance)}</strong></span></div></section>
      </div>

      {enquiryCourse && (
        <div className="paid-dashboard-enquiry-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeEnquiry(); }}>
          <section className="paid-dashboard-enquiry-modal" role="dialog" aria-modal="true" aria-labelledby="paid-dashboard-enquiry-title">
            <header>
              <div><span>Program enquiry</span><h2 id="paid-dashboard-enquiry-title">Talk to our course team</h2><p>Share your details and our team will contact you with curriculum, schedule, and enrollment guidance.</p></div>
              <button type="button" onClick={closeEnquiry} aria-label="Close enquiry form"><i className="bi bi-x-lg" /></button>
            </header>
            {enquiryState.success ? (
              <div className="paid-dashboard-enquiry-success">
                <span><i className="bi bi-check-lg" /></span>
                <h3>Enquiry submitted successfully</h3>
                <p>Your enquiry for <strong>{enquiryCourse.courseName}</strong> has been sent. Our team will contact you shortly.</p>
                <button type="button" onClick={closeEnquiry}>Done</button>
              </div>
            ) : (
              <form onSubmit={submitEnquiry}>
                <div className="paid-dashboard-enquiry-course"><i className={`bi ${courseIcon(enquiryCourse)}`} /><div><small>Selected program</small><strong>{enquiryCourse.courseName || 'Course program'}</strong></div></div>
                {enquiryState.error && <div className="paid-dashboard-enquiry-error"><i className="bi bi-exclamation-circle" /> {enquiryState.error}</div>}
                <div className="paid-dashboard-enquiry-fields">
                  <label><span>Student Name *</span><div><i className="bi bi-person" /><input value={enquiryForm.name} onChange={(event) => updateEnquiry('name', event.target.value)} autoComplete="name" /></div></label>
                  <label><span>Mobile Number *</span><div><i className="bi bi-telephone" /><input type="tel" inputMode="tel" value={enquiryForm.phone} onChange={(event) => updateEnquiry('phone', event.target.value)} autoComplete="tel" /></div></label>
                  <label><span>Email Address *</span><div><i className="bi bi-envelope" /><input type="email" value={enquiryForm.email} onChange={(event) => updateEnquiry('email', event.target.value)} autoComplete="email" /></div></label>
                  <label><span>College / Institution</span><div><i className="bi bi-building" /><input value={enquiryForm.collegeName} onChange={(event) => updateEnquiry('collegeName', event.target.value)} /></div></label>
                  <label><span>Area</span><div><i className="bi bi-geo-alt" /><input value={enquiryForm.area} onChange={(event) => updateEnquiry('area', event.target.value)} /></div></label>
                  <label className="paid-dashboard-enquiry-message"><span>Message</span><textarea rows="3" value={enquiryForm.notes} onChange={(event) => updateEnquiry('notes', event.target.value)} placeholder="Tell us what you would like to know about this program." /></label>
                </div>
                <footer><button type="button" onClick={closeEnquiry} disabled={enquiryState.saving}>Cancel</button><button type="submit" disabled={enquiryState.saving}>{enquiryState.saving ? <><span className="spinner-border spinner-border-sm" /> Submitting...</> : <>Submit Enquiry <i className="bi bi-arrow-right" /></>}</button></footer>
              </form>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
